import { useState } from "react";
import { MAX_ACCION, MAX_PREGUNTA, MAX_PRINCIPIO_ACTIVO, MAX_RECOMENDACION, errorDeAccion, errorDePregunta, errorDeRecomendacion } from "@huayruro/shared";
import { useApi, mutar } from "../../lib/useApi";
import { Button, Card, EmptyState, Input, SectionLabel, Textarea, useToast } from "../../components/ui";

// ============================================================
// Copiloto del mostrador — Ajustes (Kevin, 16-oct-2026). Lo escriben el Dueño y el Encargado; vale en
// todas las boticas de la cadena. Etapa 1: las preguntas son recordatorio (no se guardan respuestas) y
// las recomendaciones salen en el mostrador cuando lo que se lleva tiene ese principio activo.
// El contenido clínico lo pone la botica: el sistema no inventa indicaciones.
// ============================================================

type Pregunta = { id: string; texto: string; orden: number; activa: number };
type Recomendacion = { id: string; principio_activo: string; texto: string; activa: number };

export function Copiloto() {
  const datos = useApi<{ preguntas: Pregunta[]; recomendaciones: Recomendacion[]; acciones?: Recomendacion[] }>("/copiloto/ajustes");
  const toast = useToast();
  const preguntas = datos.data?.preguntas ?? [];
  const recs = datos.data?.recomendaciones ?? [];
  const acciones = datos.data?.acciones ?? [];

  const hacer = (fn: () => Promise<unknown>, ok: string): void => {
    void (async () => {
      try {
        await fn();
        toast(ok);
        datos.recargar();
      } catch (e) {
        toast(e instanceof Error ? e.message : "No se pudo guardar");
      }
    })();
  };

  return (
    <div className="flex max-w-[960px] flex-col gap-4">
      <p className="text-[13px] leading-relaxed text-ink-2">
        El copiloto está siempre a la vista en el mostrador. Las <strong>preguntas</strong> le recuerdan a quien atiende qué
        preguntar antes de vender (no se guarda lo que el cliente responde). Las <strong>recomendaciones de uso</strong> salen
        cuando lo que se lleva tiene ese principio activo: se escribe una vez para «paracetamol» y vale para todas sus marcas
        y genéricos. Lo que se escribe aquí vale en todas las boticas.
      </p>

      <Card className="gap-3">
        <SectionLabel right={<span className="text-[12px] text-ink-3 tabular-nums">{preguntas.filter((p) => p.activa === 1).length} activas</span>}>
          Preguntas antes de vender
        </SectionLabel>
        {datos.cargando ? (
          <p className="py-3 text-center text-[13px] text-ink-3">Cargando…</p>
        ) : datos.error ? (
          <p className="text-[13px] text-accent-ink">{datos.error}</p>
        ) : preguntas.length === 0 ? (
          <EmptyState title="Sin preguntas todavía" subtitle="Por ejemplo: ¿Es alérgico a algún medicamento?" />
        ) : (
          <ul className="flex flex-col divide-y divide-line-row">
            {preguntas.map((p) => (
              <FilaPregunta
                key={p.id}
                p={p}
                onGuardar={(texto) => hacer(() => mutar(`/copiloto/preguntas/${p.id}`, { method: "PATCH", body: { texto } }), "Pregunta guardada")}
                onActivar={() => hacer(() => mutar(`/copiloto/preguntas/${p.id}`, { method: "PATCH", body: { activa: p.activa !== 1 } }), p.activa === 1 ? "Pregunta apagada" : "Pregunta encendida")}
                onBorrar={() => hacer(() => mutar(`/copiloto/preguntas/${p.id}`, { method: "DELETE" }), "Pregunta borrada")}
              />
            ))}
          </ul>
        )}
        <NuevaPregunta onCrear={(texto) => hacer(() => mutar("/copiloto/preguntas", { method: "POST", body: { texto } }), "Pregunta agregada")} />
      </Card>

      <Card className="gap-3">
        <SectionLabel right={<span className="text-[12px] text-ink-3 tabular-nums">{recs.filter((r) => r.activa === 1).length} activas</span>}>
          Recomendaciones de uso por principio activo
        </SectionLabel>
        {!datos.cargando && !datos.error && recs.length === 0 && (
          <EmptyState title="Sin recomendaciones todavía" subtitle="Escribe el principio activo y lo que hay que decirle al cliente al entregarlo." />
        )}
        {recs.length > 0 && (
          <ul className="flex flex-col divide-y divide-line-row">
            {recs.map((r) => (
              <FilaRecomendacion
                key={r.id}
                r={r}
                onGuardar={(principio_activo, texto) =>
                  hacer(() => mutar(`/copiloto/recomendaciones/${r.id}`, { method: "PATCH", body: { principio_activo, texto } }), "Recomendación guardada")
                }
                onActivar={() =>
                  hacer(
                    () => mutar(`/copiloto/recomendaciones/${r.id}`, { method: "PATCH", body: { activa: r.activa !== 1 } }),
                    r.activa === 1 ? "Recomendación apagada" : "Recomendación encendida",
                  )
                }
                onBorrar={() => hacer(() => mutar(`/copiloto/recomendaciones/${r.id}`, { method: "DELETE" }), "Recomendación borrada")}
              />
            ))}
          </ul>
        )}
        <NuevaRecomendacion
          onCrear={(principio_activo, texto) =>
            hacer(() => mutar("/copiloto/recomendaciones", { method: "POST", body: { principio_activo, texto } }), "Recomendación agregada")
          }
        />
      </Card>

      {/* Buscador del Mostrador (Kevin, 16-oct-2026): la segunda línea de cada producto dice qué hace su
          principio activo. Se escribe una vez («ibuprofeno») y vale para todas sus marcas y genéricos. */}
      <Card className="gap-3">
        <SectionLabel right={<span className="text-[12px] text-ink-3 tabular-nums">{acciones.filter((a) => a.activa === 1).length} activas</span>}>
          Qué hace cada principio activo (sale en el buscador)
        </SectionLabel>
        {!datos.cargando && !datos.error && acciones.length === 0 && (
          <EmptyState title="Sin acciones todavía" subtitle="Por ejemplo: amoxicilina · Antibiótico." />
        )}
        {acciones.length > 0 && (
          <ul className="flex flex-col divide-y divide-line-row">
            {acciones.map((a) => (
              <FilaRecomendacion
                key={a.id}
                r={a}
                validar={errorDeAccion}
                maxTexto={MAX_ACCION}
                etiqueta="Acción"
                onGuardar={(principio_activo, texto) => hacer(() => mutar(`/copiloto/acciones/${a.id}`, { method: "PATCH", body: { principio_activo, texto } }), "Acción guardada")}
                onActivar={() =>
                  hacer(() => mutar(`/copiloto/acciones/${a.id}`, { method: "PATCH", body: { activa: a.activa !== 1 } }), a.activa === 1 ? "Acción apagada" : "Acción encendida")
                }
                onBorrar={() => hacer(() => mutar(`/copiloto/acciones/${a.id}`, { method: "DELETE" }), "Acción borrada")}
              />
            ))}
          </ul>
        )}
        <NuevaRecomendacion
          validar={errorDeAccion}
          maxTexto={MAX_ACCION}
          etiqueta="Acción"
          placeholder="Qué hace (por ejemplo: Analgésico y antipirético)"
          boton="Agregar acción"
          onCrear={(principio_activo, texto) => hacer(() => mutar("/copiloto/acciones", { method: "POST", body: { principio_activo, texto } }), "Acción agregada")}
        />
      </Card>
    </div>
  );
}

type Validar = (pa: string, texto: string) => string | null;

function Acciones({ activa, onEditar, onActivar, onBorrar }: { activa: boolean; onEditar: () => void; onActivar: () => void; onBorrar: () => void }) {
  const [confirmar, setConfirmar] = useState(false);
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" onClick={onEditar}>Editar</Button>
      <Button size="sm" variant="outline" onClick={onActivar}>{activa ? "Apagar" : "Encender"}</Button>
      {confirmar ? (
        <>
          <Button size="sm" onClick={onBorrar}>Sí, borrar</Button>
          <Button size="sm" variant="outline" onClick={() => setConfirmar(false)}>No</Button>
        </>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setConfirmar(true)}>Borrar</Button>
      )}
    </div>
  );
}

function FilaPregunta({ p, onGuardar, onActivar, onBorrar }: { p: Pregunta; onGuardar: (t: string) => void; onActivar: () => void; onBorrar: () => void }) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(p.texto);
  const error = editando ? errorDePregunta(texto) : null;
  return (
    <li className="flex flex-col gap-2 py-2.5">
      {editando ? (
        <>
          <Input value={texto} maxLength={MAX_PREGUNTA} onChange={(e) => setTexto(e.target.value)} aria-label="Pregunta" />
          {error && <p className="text-[12px] text-accent-ink">{error}</p>}
          <div className="flex gap-2">
            <Button size="sm" disabled={!!error} onClick={() => { onGuardar(texto); setEditando(false); }}>Guardar</Button>
            <Button size="sm" variant="outline" onClick={() => { setTexto(p.texto); setEditando(false); }}>Cancelar</Button>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className={`text-[13.5px] ${p.activa === 1 ? "text-ink" : "text-ink-3 line-through"}`}>{p.texto}</p>
          <Acciones activa={p.activa === 1} onEditar={() => setEditando(true)} onActivar={onActivar} onBorrar={onBorrar} />
        </div>
      )}
    </li>
  );
}

function FilaRecomendacion({
  r,
  onGuardar,
  onActivar,
  onBorrar,
  validar = errorDeRecomendacion,
  maxTexto = MAX_RECOMENDACION,
  etiqueta = "Recomendación",
}: {
  r: Recomendacion;
  onGuardar: (pa: string, t: string) => void;
  onActivar: () => void;
  onBorrar: () => void;
  validar?: Validar;
  maxTexto?: number;
  etiqueta?: string;
}) {
  const [editando, setEditando] = useState(false);
  const [pa, setPa] = useState(r.principio_activo);
  const [texto, setTexto] = useState(r.texto);
  const error = editando ? validar(pa, texto) : null;
  return (
    <li className="flex flex-col gap-2 py-2.5">
      {editando ? (
        <>
          <Input value={pa} maxLength={MAX_PRINCIPIO_ACTIVO} onChange={(e) => setPa(e.target.value)} aria-label="Principio activo" />
          <Textarea value={texto} maxLength={maxTexto} rows={3} onChange={(e) => setTexto(e.target.value)} aria-label={etiqueta} />
          {error && <p className="text-[12px] text-accent-ink">{error}</p>}
          <div className="flex gap-2">
            <Button size="sm" disabled={!!error} onClick={() => { onGuardar(pa, texto); setEditando(false); }}>Guardar</Button>
            <Button size="sm" variant="outline" onClick={() => { setPa(r.principio_activo); setTexto(r.texto); setEditando(false); }}>Cancelar</Button>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className={r.activa === 1 ? "" : "opacity-60"}>
            <p className="text-[13.5px] font-semibold text-ink">{r.principio_activo}</p>
            <p className="text-[13px] leading-snug text-ink-2">{r.texto}</p>
          </div>
          <Acciones activa={r.activa === 1} onEditar={() => setEditando(true)} onActivar={onActivar} onBorrar={onBorrar} />
        </div>
      )}
    </li>
  );
}

function NuevaPregunta({ onCrear }: { onCrear: (t: string) => void }) {
  const [texto, setTexto] = useState("");
  const error = texto.trim() ? errorDePregunta(texto) : null;
  return (
    <form
      className="flex flex-wrap gap-2 border-t border-line-row pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!texto.trim() || error) return;
        onCrear(texto);
        setTexto("");
      }}
    >
      <Input
        value={texto}
        maxLength={MAX_PREGUNTA}
        onChange={(e) => setTexto(e.target.value)}
        placeholder="¿Para quién es el medicamento?"
        aria-label="Nueva pregunta"
        className="min-w-[240px] flex-1"
      />
      <Button type="submit" size="sm" disabled={!texto.trim() || !!error}>Agregar pregunta</Button>
      {error && <p className="w-full text-[12px] text-accent-ink">{error}</p>}
    </form>
  );
}

function NuevaRecomendacion({
  onCrear,
  validar = errorDeRecomendacion,
  maxTexto = MAX_RECOMENDACION,
  etiqueta = "Recomendación",
  placeholder = "Lo que se le dice al cliente al entregarlo",
  boton = "Agregar recomendación",
}: {
  onCrear: (pa: string, t: string) => void;
  validar?: Validar;
  maxTexto?: number;
  etiqueta?: string;
  placeholder?: string;
  boton?: string;
}) {
  const [pa, setPa] = useState("");
  const [texto, setTexto] = useState("");
  const lleno = pa.trim() !== "" && texto.trim() !== "";
  const error = lleno ? validar(pa, texto) : null;
  return (
    <form
      className="flex flex-col gap-2 border-t border-line-row pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!lleno || error) return;
        onCrear(pa, texto);
        setPa("");
        setTexto("");
      }}
    >
      <Input value={pa} maxLength={MAX_PRINCIPIO_ACTIVO} onChange={(e) => setPa(e.target.value)} placeholder="Principio activo (por ejemplo: paracetamol)" aria-label={etiqueta === "Recomendación" ? "Principio activo nuevo" : `Principio activo de la ${etiqueta.toLowerCase()} nueva`} />
      <Textarea
        value={texto}
        maxLength={maxTexto}
        rows={etiqueta === "Acción" ? 2 : 3}
        onChange={(e) => setTexto(e.target.value)}
        placeholder={placeholder}
        aria-label={`${etiqueta} nueva`}
      />
      {error && <p className="text-[12px] text-accent-ink">{error}</p>}
      <Button type="submit" size="sm" disabled={!lleno || !!error} className="self-start">{boton}</Button>
    </form>
  );
}
